import { FC, PropsWithChildren, SyntheticEvent, useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import {
  ExpandMore,
  OpenInNew,
  PrecisionManufacturingOutlined,
  WarningAmberOutlined,
  FactCheckOutlined,
  SsidChartOutlined,
  SvgIconComponent,
} from "@mui/icons-material";

import { IUser } from "@framework/types";
import { useUser } from "@framework/provider-user";
import { useSettings } from "@framework/provider-settings";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { sanitizeSchemePageView } from "../../utils/scheme-page-view";
import { StyledAccordion, StyledAccordionDetails, StyledAccordionSummary } from "./styled";

declare global {
  interface Window {
    openedWindowsCount?: number;
  }
}

export interface IDashboardAccordion {
  expanded: boolean;
  isLoading?: boolean;
  setExpanded: (expanded: boolean) => void;
  title: string;
  componentName: string;
  isNarrow?: boolean;
}

const keysMapper: Record<string, keyof IUser["interfaceSettings"]> = {
  "1": "showTrainScheme",
  "2": "showStatusAndForecasting",
  "3": "showTableData",
  "4": "showMeasureData",
};

const componentIconMap: Record<string, SvgIconComponent> = {
  train: PrecisionManufacturingOutlined,
  stateAndForecast: WarningAmberOutlined,
  tables: FactCheckOutlined,
  trends: SsidChartOutlined,
};

export const DashboardAccordion: FC<PropsWithChildren<IDashboardAccordion>> = props => {
  const { children, expanded, isLoading, setExpanded, title, componentName, isNarrow } = props;

  const { profile } = useUser<IUser>();
  const showTabsSeparately = profile.interfaceSettings.showTabsSeparately;

  const settings = useSettings();
  const schemeColumnCount = settings.getSchemeColumnCount();
  const colsPerRow = Number(schemeColumnCount);

  const componentsOrder = useMemo(() => {
    return sanitizeSchemePageView(profile.interfaceSettings.schemePageView).filter(
      id => profile.interfaceSettings[keysMapper[id]],
    );
  }, [profile.interfaceSettings]);

  const totalComponents = componentsOrder.length;

  const isSingleColumnManyComponents = colsPerRow === 1 && totalComponents > 2;

  const handleChange = (event: SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const Icon = componentIconMap[componentName];

  const renderTitle = () => {
    if (!expanded && Icon && isNarrow) {
      return (
        <Tooltip title={title} arrow>
          <Icon fontSize="small" />
        </Tooltip>
      );
    }

    return (
      <Typography fontWeight={700} lineHeight="21px" color="#363846">
        {title}
      </Typography>
    );
  };

  const openInNewWindow = () => {
    if (!showTabsSeparately) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("onlyPage", "true");
    currentUrl.searchParams.set("component", componentName);

    const width = 800;
    const height = 600;

    // Инициализация и управление счетчиком окон
    if (typeof window.openedWindowsCount !== "number") {
      window.openedWindowsCount = 0;
    }
    window.openedWindowsCount += 1;

    // Вычисление позиции с учетом границ экрана
    const baseLeft = Math.max(0, (window.screen.width - width) / 2);
    const baseTop = Math.max(0, (window.screen.height - height) / 2);

    // Вычисление максимального количества шагов смещения с учетом границ экрана
    const stepSize = 30;
    const maxOffset = Math.min(
      Math.floor((window.screen.width - width - 50) / stepSize),
      Math.floor((window.screen.height - height - 50) / stepSize),
    );

    // Циклическое смещение с возвратом к началу при достижении границы
    const offsetCount = (window.openedWindowsCount - 1) % Math.max(1, maxOffset);
    const offset = offsetCount * stepSize;

    const left = baseLeft + offset;
    const top = baseTop + offset;

    const newWindow = window.open(
      currentUrl.toString(),
      `${componentName}_window_${Date.now()}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,dependent=no,location=yes,fullscreen=no`,
    );

    if (newWindow) {
      // Отслеживаем закрытие окна
      newWindow.addEventListener("beforeunload", () => {
        if (typeof window.openedWindowsCount === "number" && window.openedWindowsCount > 0) {
          window.openedWindowsCount -= 1;
        }
      });

      newWindow.focus();
    }
  };

  return (
    <Box sx={{ m: 0, width: "100%", height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <ProgressOverlay
        isLoading={!!isLoading}
        wrapperSx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <StyledAccordion expanded={expanded} onChange={handleChange} elevation={0}>
          <StyledAccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls="panel1a-content"
            id={`panel1a-header-${title}`}
          >
            {renderTitle()}
            {showTabsSeparately && (
              <OpenInNew
                fontSize="small"
                sx={{
                  color: "#363846",
                  opacity: 0.7,
                  cursor: "pointer",
                  ml: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "4px",
                }}
                onClick={e => {
                  e.stopPropagation();
                  openInNewWindow();
                }}
              />
            )}
          </StyledAccordionSummary>
          <StyledAccordionDetails sx={{ overflow: isSingleColumnManyComponents ? "auto" : "hidden" }}>
            {children}
          </StyledAccordionDetails>
        </StyledAccordion>
      </ProgressOverlay>
    </Box>
  );
};
